import { useForm } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import Button from "@/components/buttons/Button";
import InertiaLink from "@/components/buttons/InertiaLink";
import SplitShares, { ShareOption } from "@/components/inputs/SplitShares";
import { Artwork } from "@/components/ArtworkSlot";
import FormActions from "@/components/buttons/FormActions";

interface RightsEditProps {
  track: {
    id: number;
    title: string;
    artwork_attached: boolean;
  };
  artwork: Artwork;
  current_writers: ShareOption[];
  current_master_owners: ShareOption[];
}

const Edit: React.FC<RightsEditProps> = ({
  track,
  artwork,
  current_writers,
  current_master_owners,
}) => {
  const { data, setData, put, processing, errors, transform } = useForm({
    writers: current_writers,
    master_owners: current_master_owners,
  });

  transform((data) => ({
    writers: data.writers.map((w) => ({
      contributor_id: w.value,
      split_percentage: w.split_percentage,
    })),
    master_owners: data.master_owners.map((mo) => ({
      contributor_id: mo.value,
      split_percentage: mo.split_percentage,
    })),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/tracks/${track.id}/rights`);
  };

  const handleCopyMasterOwnersToWriters = () => {
    setData(
      "writers",
      data.master_owners.map((owner) => ({ ...owner })),
    );
  };

  const handleCopyWritersToMasterOwners = () => {
    setData(
      "master_owners",
      data.writers.map((writer) => ({ ...writer })),
    );
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{
          href: `/tracks/${track.id}/rights`,
          text: "Back to rights",
        }}
        title={`Edit ${track.title} Rights`}
        secondaryAction={{
          href: `/tracks/${track.id}/rights`,
          text: "Cancel",
        }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-5 md:gap-10"
      >
        <div className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
          <div className="flex w-full flex-col items-start gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <h2 className="text-cream-900 dark:text-cream-100 text-lg font-semibold">
              Master Owners
            </h2>
            <Button
              text="Copy from writers"
              type="secondary"
              htmlButtonType="button"
              className="w-full md:w-auto"
              onClick={handleCopyWritersToMasterOwners}
              disabled={data.writers.length === 0}
            />
          </div>
          <SplitShares
            shares={data.master_owners}
            onChange={(master_owners) =>
              setData("master_owners", master_owners)
            }
            reloadPropName="master_owner_search_results"
            queryParam="master_owner_q"
            placeholder="Search for master owners"
            errors={errors.master_owners}
            baseErrors={(errors as Record<string, string>).master_owner_base}
          />
        </div>
        <div className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
          <div className="flex w-full flex-col items-start gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <h2 className="text-cream-900 dark:text-cream-100 text-lg font-semibold">
              Writers
            </h2>
            <Button
              text="Copy from master owners"
              type="secondary"
              htmlButtonType="button"
              className="w-full md:w-auto"
              onClick={handleCopyMasterOwnersToWriters}
              disabled={data.master_owners.length === 0}
            />
          </div>
          <SplitShares
            shares={data.writers}
            onChange={(writers) => setData("writers", writers)}
            reloadPropName="writer_search_results"
            queryParam="writer_q"
            placeholder="Search for writers"
            errors={errors.writers}
            baseErrors={(errors as Record<string, string>).writer_base}
          />
        </div>
        <FormActions>
          <InertiaLink
            href={`/tracks/${track.id}/rights`}
            type="secondary"
            text="Cancel"
          />
          <Button
            text={processing ? "Saving..." : "Save"}
            type="primary"
            htmlButtonType="submit"
            disabled={processing}
          />
        </FormActions>
      </form>
    </div>
  );
};

export default Edit;
